import type { FC } from 'react';
import { useCallback } from 'react';
import { Link, useBeforeUnload } from 'react-router-dom';
import Tooltip from '@components/Tooltip';
import { CircleIconWrapper } from '@ContractBuilder/contract-builder-header/components/CircleIconWrapper';
import type { DbLibraryBlock } from '@domain/schemas/block.schema';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/outline';
import { formatDate } from '@root/helpers/date';
import { getFeatureFlag } from '@root/helpers/feature-flags/get-feature-flag';
import sharedClasses from '@root/src/utils/shared-classes';
import { isClausesPath } from '@utils/app-paths';
import clsx from 'clsx';
import { get } from 'lodash-es';

import Nav from '../../../routes';
import { LibraryBlockEditableNameField } from '../components/LibraryBlockEditableNameField';
import { UNSAVED_CHANGES_PROMPT } from '../constants';

interface LibraryBlockEditHeaderProps {
  canNavigate: boolean;
  data: DbLibraryBlock;
  isActivityLogOpen: boolean;
  onUpdate?: () => Promise<any> | void;
  toggleVisibilityOfActivityLog: () => void;
}

export const LibraryBlockEditHeader: FC<LibraryBlockEditHeaderProps> = ({
  canNavigate,
  data,
  isActivityLogOpen,
  onUpdate,
  toggleVisibilityOfActivityLog,
}) => {
  const isClause = isClausesPath();
  const isActivityLogEnabled = getFeatureFlag('LibraryBlockActivityLog');
  const lastUpdatedAtInfo = formatDate(data.updated_at, 'DD MMMM YYYY');
  const lastUpdatedByInfo = get(data, 'last_updated_by.name') ?? get(data, 'created_by.name');

  const dateInfo = `Last updated ${lastUpdatedAtInfo}${lastUpdatedByInfo ? ` by ${lastUpdatedByInfo}` : ''}`;
  const title = isClause ? 'Back to Clauses' : 'Back to Blocks';

  const handleUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!canNavigate) {
        event.preventDefault();
        return (event.returnValue = UNSAVED_CHANGES_PROMPT);
      }
    },
    [canNavigate],
  );

  useBeforeUnload(handleUnload);

  return (
    <>
      <div className="flex flex-col border-b bg-white px-5 py-[14px]">
        <div className="flex w-full flex-nowrap items-center gap-3">
          <div className={clsx(!canNavigate && sharedClasses.disabledContainer)}>
            <Tooltip content={isClause ? 'Back to Clauses' : 'Back to Blocks'}>
              <Link
                to={isClause ? Nav.Clauses : Nav.Blocks}
                className={clsx('group', !canNavigate && sharedClasses.disabled)}
              >
                <div className={'border-info-600 mr-2 rounded-full border-2 p-1 group-hover:bg-gray-100'} title={title}>
                  <ArrowLeftIcon className="text-info-600 h-4 w-4" strokeWidth="3" />
                </div>
              </Link>
            </Tooltip>
          </div>
          <div className="flex min-w-0 items-center gap-4 2xl:flex-nowrap">
            <LibraryBlockEditableNameField data={data} hasOperationsDisabled={!canNavigate} onRefetch={onUpdate} />
            <p className="text-info-400 ml-4 cursor-default text-sm font-light whitespace-nowrap 2xl:block">
              {dateInfo}
            </p>
          </div>
          {isActivityLogEnabled && (
            <div className="ml-auto">
              <Tooltip content={`${isActivityLogOpen ? 'Hide' : 'Show'} activity`}>
                <CircleIconWrapper
                  data-testid="activity-log-button"
                  icon={ClockIcon}
                  isActive={isActivityLogOpen}
                  onClick={toggleVisibilityOfActivityLog}
                />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
